import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IBranchModel } from 'src/app/logic/branch/models/branch.model';
import {
  formatMexicoCollectionScheduleDetail,
  mexicoWallClockToIso,
} from 'src/app/shared/utils/mexico-datetime.util';
import { ExchangeRateCalculatorService } from 'src/app/services/exchange-rate-calculator.service';
import { IExchangeModel } from 'src/app/logic/exchanges/model/exchange.model';
import { isCentenarioCurrencyCode } from 'src/app/utils/centenario-currency-display';

@Component({
  selector: 'app-reservation-confirmation',
  templateUrl: './reservation-confirmation.component.html',
  styleUrls: ['./reservation-confirmation.component.scss'],
})
export class ReservationConfirmationComponent {
  protected readonly isCentenarioCurrencyCode = isCentenarioCurrencyCode;

  @Input() cotizacion!: any;
  @Input() sucursal!: IBranchModel;
  @Input() selectedTimeSlot: any;
  @Input() branchTimezone = 'America/Mexico_City';
  @Input() estimatedPoints: number = 0;
  @Input() exchanges: IExchangeModel[] = [];

  @Output() canceled = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<void>();

  constructor(
    private readonly exchangeRateCalculator: ExchangeRateCalculatorService,
    private readonly translate: TranslateService,
  ) {}

  get formattedSchedule(): string {
    if (!this.selectedTimeSlot) {
      return '';
    }

    const start = mexicoWallClockToIso(
      this.selectedTimeSlot.date,
      this.selectedTimeSlot.timeFrom,
      this.branchTimezone,
    );
    const end = mexicoWallClockToIso(
      this.selectedTimeSlot.date,
      this.selectedTimeSlot.timeto,
      this.branchTimezone,
    );

    if (!start || !end) {
      return '';
    }

    return formatMexicoCollectionScheduleDetail(
      start,
      end,
      this.getLocaleTag(),
      this.branchTimezone,
    );
  }

  getFormattedSchedule(): string {
    if (!this.formattedSchedule) {
      return '';
    }

    const parts = this.formattedSchedule.split(' - ');
    if (parts.length === 2) {
      const start = parts[0].trim();
      const end = parts[1].trim();
      const sep = this.translate.instant(
        'PICKUP_SCHEDULE.TIME_RANGE_SEPARATOR',
      );
      return `${start} ${sep} ${end}`;
    }

    return this.formattedSchedule;
  }

  private getLocaleTag(): string {
    const lang =
      this.translate.currentLang ||
      this.translate.defaultLang ||
      'es';
    return lang.startsWith('en') ? 'en-US' : 'es-MX';
  }

  get fromAmount(): number {
    if (!this.cotizacion) {
      return 0;
    }
    return this.exchangeRateCalculator.getFromAmountForQuote(this.cotizacion);
  }

  get toAmount(): number {
    if (!this.cotizacion) {
      return 0;
    }
    return this.exchangeRateCalculator.getToAmountForQuote(this.cotizacion);
  }

  get documentationAmountUsd(): number {
    if (!this.cotizacion) {
      return 0;
    }
    return this.exchangeRateCalculator.getUsdNotionalForQuote(
      this.cotizacion,
      Array.isArray(this.exchanges) ? this.exchanges : [],
    );
  }

  get fullAddress(): string {
    if (!this.sucursal?.address) {
      return '';
    }
    const address = this.sucursal.address;
    return `${address.street || ''}, ${
      address.sublocality || address.borough || ''
    }, ${address.city || ''}, ${address.state || ''}`.trim();
  }

  handleCancel(): void {
    this.canceled.emit();
  }

  handleConfirm(): void {
    this.confirmed.emit();
  }
}
